import { useDateFormat } from '@vueuse/core'
import { getPlatforms } from '@ionic/vue'
import allLanguages from '../locales/languageIds.json'
import { fetchWrapper } from '@/helpers/fetch-wrapper.js'
import { LingoCapacitor } from 'lingo-capacitor'
import { useAuthStore} from '@/stores/auth.store'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { AdMob, RewardAdPluginEvents, RewardInterstitialAdPluginEvents } from '@capacitor-community/admob';
import { inject } from 'vue';
import { env } from '@/helpers/env.js'
const baseEnv = env

const timeDisplay = 'YYYY-MM-DD HH:mma'
const dateDisplay = 'YYYY-MM-DD'

function standardizeDate(time) {
  if (typeof time === 'string') {
    time = new Date(time)
  }
  return useDateFormat(time, dateDisplay).value
}

function standardizeTime(time) {
  if (typeof time === 'string') {
    time = new Date(time)
  }
  return useDateFormat(time, timeDisplay).value
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

function checkIsApp() {
  if (getPlatforms().includes('capacitor')) {
    return true
  }
  return false
}

function checkIsDesktop() {
  if (getPlatforms().includes('desktop')) {
    return true
  }
  return false
}

function checkIsIosApp() {
  if (getPlatforms().includes('capacitor')) {
    if (getPlatforms().includes('ios')) {
      return true
    }
  }
  return false
}

function checkIsAndroidApp() {
  if (getPlatforms().includes('capacitor')) {
    if (getPlatforms().includes('android')) {
      return true
    }
  }
  return false
}

function getLanguageIdByName(language) {
  return allLanguages.find(lang => lang.language === language).id
}

async function markTourViewed(tour) {
  var field_name = 'shown_' + tour + '_tour'
  await fetchWrapper.patch('/profile/', {
    [field_name]: true
  })
}

async function markAutoProductDisplayComplete() {
  var field_name = 'auto_product_display'
  await fetchWrapper.patch('/profile/', {
    [field_name]: true
  })
}

async function getSupportedKeyboards() {
  const { supportedLanguagesByCode } = useAuthStore()
  const input_modes = await LingoCapacitor.getAllInputModes()
  if (input_modes.inputModes.includes('web')) {
    return []
    // return ['en', 'es']
  } else {
    var keyboards = input_modes.inputModes.filter((item) => item !== 'emoji')
    var languageKeyboards = []
    for (var i = 0; i < keyboards.length; i++) {
      var supportedKeyboard = keyboards[i]
      // discard anything after the first hyphen if it exists
      if (supportedKeyboard.includes('-')) {
        supportedKeyboard = supportedKeyboard.split('-')[0]
      }
      for (const key in supportedLanguagesByCode) {
        console.log('compare', supportedKeyboard, key, supportedLanguagesByCode[key])
        if (supportedKeyboard === key) {
          languageKeyboards.push(supportedLanguagesByCode[key])
          break
        }
      }
    }
  }
  return languageKeyboards
}

async function fireHaptic() {
  if (isApp) {
    console.log('haptic firing')
    await Haptics.vibrate()
  }
}


const isApp = checkIsApp()
const isDesktop = checkIsDesktop()
const isIosApp = checkIsIosApp()
const isAndroidApp = checkIsAndroidApp()

const adMobTesting = baseEnv.VUE_APP_ENVIRONMENT !== 'production'

// Alternative approach: Create a simple event emitter in utils.js
const adEventBus = {
  callbacks: [],
  emit(event) {
    this.callbacks.forEach(cb => cb(event))
  },
  on(callback) {
    this.callbacks.push(callback)
  },
  off(callback) {
    this.callbacks = this.callbacks.filter(cb => cb !== callback)
  }
}

// Add web ad state management
const webAdState = {
  scriptLoaded: false,
  adKey: 0,
  isTestMode: adMobTesting
}

// Load AdSense script function (from WebRewardInterstitial)
const loadAdSenseScript = () => {
  if (!window.adsbygoogle && !webAdState.scriptLoaded) {
    console.log('Loading AdSense script...')
    webAdState.scriptLoaded = true
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6734004389316610'
    script.crossOrigin = 'anonymous'
    document.head.appendChild(script)
  }
}

// Create a new function for the integrated approach
const showRewardInterstitialIntegrated = async (creditType) => {
  if (!isApp) {
    console.log('Preparing web interstitial ad (integrated approach)')
    
    // Increment key to force new ad container
    webAdState.adKey++
    
    // Load script only when showing interstitial
    loadAdSenseScript()
    
    // Return configuration for RewardNotifications to use
    return {
      type: 'web',
      adKey: webAdState.adKey,
      isTestMode: webAdState.isTestMode,
      maxRetries: 1,
      loadAd: (containerId, callbacks) => {
        setTimeout(() => {
          console.log('Initializing AdSense ad')
          try {
            const adElement = document.querySelector(`#${containerId} .adsbygoogle`)
            if (adElement && !adElement.hasAttribute('data-adsbygoogle-status')) {
              // Push ad with test parameters if in test mode
              const adPush = {
                google_ad_client: 'ca-pub-6734004389316610',
                google_ad_slot: '4898069421',
              }
              
              if (webAdState.isTestMode) {
                adPush.google_adtest = 'on'
              }
              (window.adsbygoogle = window.adsbygoogle || []).push(adPush)
              
              // Monitor ad status
              const checkAdStatus = setInterval(() => {
                const status = adElement.getAttribute('data-ad-status')
                if (status) {
                  clearInterval(checkAdStatus)
                  callbacks.onStatusChange?.(status)
                  
                  if (status === 'filled') {
                    callbacks.onSuccess?.()
                  } else if (status === 'unfilled') {
                    callbacks.onUnfilled?.()
                  }
                }
              }, 100)
              
              // Stop checking after 5 seconds
              setTimeout(() => {
                clearInterval(checkAdStatus)
                const currentStatus = adElement.getAttribute('data-ad-status')
                if (!currentStatus) {
                  callbacks.onTimeout?.()
                }
              }, 5000)
              
            } else {
              callbacks.onError?.('Ad element not ready')
            }
          } catch (err) {
            console.error('Error loading AdSense ad:', err)
            callbacks.onError?.(err)
          }
        }, 500)
      }
    }
  }
  
  console.log('Preparing app interstitial ad (integrated approach)')
    
  // Mobile app logic (same as before)
  var ad_unit_id
  if (isIosApp) {
    ad_unit_id = 'ca-app-pub-6734004389316610/2775502801'
  } else {
    ad_unit_id = 'ca-app-pub-6734004389316610/4823398875'
  } 
  
  // Set up the reward listener FIRST
  const rewardListener = await AdMob.addListener(RewardInterstitialAdPluginEvents.Rewarded, async (reward) => {
    console.log('User earned reward integrated:', reward)
    // Add credits when user completes watching the ad
    await addUserCredits(creditType)
    // Remove this specific listener after use
    rewardListener.remove()
  });
  
  try {
    await AdMob.prepareRewardInterstitialAd({
      adId: ad_unit_id,
      isTesting: adMobTesting,
      npa: true
    });
    
    await AdMob.showRewardInterstitialAd();
    return { type: 'mobile' }
  } catch (error) {
    console.error('Error showing reward ad:', error)
    // Clean up listener if ad fails
    rewardListener.remove()
    throw error
  }
}

// Add this new function after the other utility functions
async function addUserCredits(credit_type) {
  try {
    console.log('addUserCredits called with ' + credit_type)
    const authStore = useAuthStore()
    if (!authStore.user) {
      console.error('No authenticated user found')
      return false
    }
    
    const response = await fetchWrapper.post('/billing/add-credits/', {
      credit_type: credit_type
    })
    console.log('Credits added successfully:', response)
    
    // Update the user's credit count in the store if the response includes it
    if (response.total_credits !== undefined) {
      authStore.user.total_credits = response.total_credits
    }
    
    return true
  } catch (error) {
    console.error('Error adding credits:', error)
    return false
  }
}

// Update the showRewardInterstitial function
const showRewardInterstitial = async () => {
  if (!isApp) {
    console.log('Emitting show web interstitial event')
    adEventBus.emit('show-web-interstitial')
    return
  } 
  
  // Prepare the ad
  var ad_unit_id
  if (isIosApp) {
    ad_unit_id = 'ca-app-pub-6734004389316610/2775502801' // reward interstitial
  } else {
    ad_unit_id = 'ca-app-pub-6734004389316610/4823398875'
  } 
  
  // Set up the reward listener FIRST before any other operations
  const rewardListener = await AdMob.addListener(RewardInterstitialAdPluginEvents.Rewarded, async (reward) => {
    console.log('User earned reward: old method', reward)
    // Add credits when user completes watching the ad
    await addUserCredits()
    // Remove this specific listener after use
    rewardListener.remove()
  });
  
  try {
    await AdMob.prepareRewardInterstitialAd({
      adId: ad_unit_id,
      isTesting: adMobTesting,
      npa: true
    });
    
    // Show the ad
    await AdMob.showRewardInterstitialAd();
  } catch (error) {
    console.error('Error showing reward ad:', error)
    // Clean up listener if ad fails
    rewardListener.remove()
  }
};

// Update exports
export {
  standardizeTime,
  standardizeDate,
  capitalizeFirstLetter,
  isApp,
  isDesktop,
  isIosApp,
  isAndroidApp,
  getLanguageIdByName,
  markTourViewed,
  markAutoProductDisplayComplete,
  getSupportedKeyboards,
  fireHaptic,
  adEventBus,
  showRewardInterstitial,
  showRewardInterstitialIntegrated,
  addUserCredits // Add new export
}
